/**
 * useDocumentationSettings – Full practice-level documentation settings hook.
 *
 * Reads/writes documentation settings JSON on the practitioner's linked
 * Organization (extension `template-settings-data`). Falls back to legacy
 * Basic resources for reads only; saves always migrate to Organization.
 */
import { useCallback, useEffect, useState } from 'react';
import { useMedplum } from '@medplum/react-hooks';
import {
  DEFAULT_DOCUMENTATION_SETTINGS,
  type DocumentationSettings,
} from '../types';
import { migrateFooterMacros } from '../utils/documentFormat';
import {
  loadDocumentationSettingsRaw,
  saveDocumentationSettingsRaw,
} from '../utils/documentationSettingsStorage';
import { usePracticeOrganization } from './usePracticeOrganization';

interface UseDocumentationSettingsResult {
  settings: DocumentationSettings;
  loading: boolean;
  error: string | null;
  saveSettings: (settings: DocumentationSettings) => Promise<void>;
  refetch: () => void;
}

function mergeWithDefaults(partial: unknown): DocumentationSettings {
  if (!partial || typeof partial !== 'object') {
    return { ...DEFAULT_DOCUMENTATION_SETTINGS };
  }
  const raw = partial as Record<string, unknown>;

  const enabledCategories = {
    ...DEFAULT_DOCUMENTATION_SETTINGS.enabledCategories,
    ...(typeof raw.enabledCategories === 'object' && raw.enabledCategories !== null
      ? (raw.enabledCategories as Record<string, boolean>)
      : {}),
  };
  const rawEnabled =
    typeof raw.enabledCategories === 'object' && raw.enabledCategories !== null
      ? (raw.enabledCategories as Record<string, boolean>)
      : undefined;
  if (rawEnabled && typeof rawEnabled['client-operations'] === 'boolean' && rawEnabled.administrative === undefined) {
    enabledCategories.administrative = rawEnabled['client-operations'];
  }

  const storedFormat = typeof raw.documentFormat === 'object' && raw.documentFormat !== null
    ? (raw.documentFormat as Record<string, unknown>)
    : {};

  const documentFormat = {
    standardFormat: {
      ...DEFAULT_DOCUMENTATION_SETTINGS.documentFormat.standardFormat,
      ...(typeof storedFormat.standardFormat === 'object' && storedFormat.standardFormat !== null
        ? (storedFormat.standardFormat as Record<string, unknown>)
        : {}),
      ...(typeof raw.standardFormat === 'object' && raw.standardFormat !== null
        ? (raw.standardFormat as Record<string, unknown>)
        : {}),
    },
    preLicensedFormat: {
      ...DEFAULT_DOCUMENTATION_SETTINGS.documentFormat.preLicensedFormat,
      ...(typeof storedFormat.preLicensedFormat === 'object' && storedFormat.preLicensedFormat !== null
        ? (storedFormat.preLicensedFormat as Record<string, unknown>)
        : {}),
      ...(typeof raw.preLicensedFormat === 'object' && raw.preLicensedFormat !== null
        ? (raw.preLicensedFormat as Record<string, unknown>)
        : {}),
    },
  } as DocumentationSettings['documentFormat'];

  documentFormat.standardFormat.footerText = migrateFooterMacros(
    String(documentFormat.standardFormat.footerText ?? ''),
  );
  documentFormat.preLicensedFormat.footerText = migrateFooterMacros(
    String(documentFormat.preLicensedFormat.footerText ?? ''),
  );

  const templateRules = {
    ...DEFAULT_DOCUMENTATION_SETTINGS.templateRules,
    ...(typeof raw.templateRules === 'object' && raw.templateRules !== null
      ? (raw.templateRules as Record<string, unknown>)
      : {}),
  } as DocumentationSettings['templateRules'];

  const defaultTemplateByContext = Array.isArray(raw.defaultTemplateByContext)
    ? raw.defaultTemplateByContext
    : [];

  return {
    enabledCategories,
    documentFormat,
    templateRules,
    defaultTemplateByContext,
  };
}

function parseSettingsJson(json: string | null): DocumentationSettings {
  if (!json) return { ...DEFAULT_DOCUMENTATION_SETTINGS };
  try {
    return mergeWithDefaults(JSON.parse(json));
  } catch {
    return { ...DEFAULT_DOCUMENTATION_SETTINGS };
  }
}

export function useDocumentationSettings(): UseDocumentationSettingsResult {
  const medplum = useMedplum();
  const {
    organization,
    organizationId,
    loading: orgLoading,
    refetch: refetchOrganization,
  } = usePracticeOrganization();
  const [settings, setSettings] = useState<DocumentationSettings>(DEFAULT_DOCUMENTATION_SETTINGS);
  const [legacyBasicId, setLegacyBasicId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refetchCounter, setRefetchCounter] = useState(0);

  const refetch = useCallback(() => {
    setRefetchCounter((c) => c + 1);
    refetchOrganization();
  }, [refetchOrganization]);

  useEffect(() => {
    if (orgLoading) return;

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const loaded = await loadDocumentationSettingsRaw(medplum, organization);
        if (cancelled) return;

        setLegacyBasicId(loaded.legacyBasicId);
        setSettings(parseSettingsJson(loaded.settingsJson));
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to fetch documentation settings');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => { cancelled = true; };
  }, [medplum, organization, orgLoading, refetchCounter]);

  const saveSettings = useCallback(
    async (newSettings: DocumentationSettings): Promise<void> => {
      const orgId = organizationId ?? organization?.id;
      if (!orgId) {
        throw new Error(
          'No practice Organization found. Seed the Medplum project or ensure your account is linked to a practice.',
        );
      }

      const json = JSON.stringify(newSettings);
      await saveDocumentationSettingsRaw(medplum, orgId, json, legacyBasicId);
      setLegacyBasicId(null);
      setSettings(newSettings);
      refetchOrganization();
    },
    [medplum, organization, organizationId, legacyBasicId, refetchOrganization],
  );

  return {
    settings,
    loading: orgLoading || loading,
    error,
    saveSettings,
    refetch,
  };
}
